import {
  API_PREFIX,
  DEFAULT_LOCALE,
  LOCALE_HEADER,
  ROUTES,
  type ApiError,
  type ConflictDetails,
  type Locale,
  type TokenPair,
} from '@arriba.digital/contracts';
import { session } from './session';

/**
 * Base URL. Defaults to a same-origin path so the Vite dev proxy handles it
 * locally and nginx does so in production; `VITE_API_BASE_URL` overrides it
 * when the API lives on a different host.
 */
export const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? `/${API_PREFIX}`;

/** An error the API reported, carrying its stable code and translated text. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  /** True when the caller is not (or no longer) authenticated. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Field-level validation issues, when the failure was a schema violation. */
  get validationIssues(): Array<{
    field: string;
    message: string;
    code: string;
  }> {
    const issues = (this.details as { issues?: unknown } | undefined)?.issues;
    return Array.isArray(issues)
      ? (issues as Array<{ field: string; message: string; code: string }>)
      : [];
  }

  /**
   * Optimistic-concurrency context, when the failure was a stale write.
   *
   * A conflict is the one error the UI has to act on rather than merely
   * report: this is what lets the editor say *who* moved the record and offer
   * to reload it. `null` when the 409 was about something else entirely.
   */
  get conflict(): ConflictDetails | null {
    const details = this.details as Partial<ConflictDetails> | undefined;
    if (!details || (details.conflict !== 'stale' && details.conflict !== 'deleted')) {
      return null;
    }
    return details as ConflictDetails;
  }

  /** The article that already owns a duplicate SKU or barcode, if any. */
  get duplicateCode(): {
    productId: string;
    productName: string;
    isActive?: boolean | null;
  } | null {
    const details = this.details as
      { productId?: unknown; productName?: unknown; isActive?: unknown } | undefined;

    if (typeof details?.productId !== 'string' || typeof details?.productName !== 'string') {
      return null;
    }

    return {
      productId: details.productId,
      productName: details.productName,
      isActive: typeof details.isActive === 'boolean' ? details.isActive : null,
    };
  }
}

/** Raised when the API cannot be reached at all (offline, DNS, CORS). */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('errors.network');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();

/** Notified when the session cannot be recovered, so the app can route to login. */
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

let currentLocale: Locale = DEFAULT_LOCALE;

export function setRequestLocale(locale: Locale): void {
  currentLocale = locale;
}

/* ------------------------------------------------------------------ *
 * Token refresh
 * ------------------------------------------------------------------ */

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchange the refresh token for a new pair.
 *
 * Single-flight: a screen that fires six requests at once after the access
 * token expires must trigger exactly one refresh, otherwise the rotated
 * refresh tokens race and five of the six attempts are rejected.
 */
async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  const run = async (): Promise<boolean> => {
    const refreshToken = session.refreshToken;
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${BASE_URL}/${ROUTES.auth.refresh}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [LOCALE_HEADER]: currentLocale,
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        session.clear();
        return false;
      }

      const tokens = (await response.json()) as TokenPair;
      session.setTokens(tokens.accessToken, tokens.refreshToken);
      return true;
    } catch {
      return false;
    }
  };

  refreshInFlight = run().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/* ------------------------------------------------------------------ *
 * Request
 * ------------------------------------------------------------------ */

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
  /**
   * Let the request outlive the page that made it.
   *
   * Used by the presence release on `pagehide`: a browser cancels ordinary
   * in-flight fetches when a document is torn down, and without this the
   * release would never reach the server and the colleague would keep seeing
   * an editor who has already left.
   */
  keepalive?: boolean;
  /** Skip the Authorization header (used by login). */
  anonymous?: boolean;
  /**
   * Send this token instead of the stored session one.
   *
   * The onboarding ticket is a bearer credential that is deliberately not a
   * session: it can choose a country and nothing else. It goes in the
   * `Authorization` header like any other token, but the request must not
   * trigger the session refresh path, so this is always paired with
   * `anonymous: true`.
   */
  bearer?: string;
  /** Internal: prevents an infinite refresh loop. */
  retried?: boolean;
}

function buildUrl(path: string, query?: Record<string, unknown>): string {
  const url = `${BASE_URL}/${path}`;
  if (!query) {
    return url;
  }

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(key, String(item));
      }
    } else if (value instanceof Date) {
      params.append(key, value.toISOString());
    } else {
      params.append(key, String(value));
    }
  }

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, anonymous, retried, keepalive } = options;

  const headers: Record<string, string> = { [LOCALE_HEADER]: currentLocale };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (options.bearer) {
    headers.Authorization = `Bearer ${options.bearer}`;
  } else if (!anonymous) {
    const token = session.accessToken;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      signal,
      keepalive,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw new NetworkError(error);
  }

  // One transparent refresh-and-retry on an expired access token.
  if (response.status === 401 && !anonymous && !retried && session.refreshToken) {
    const recovered = await refreshSession();
    if (recovered) {
      return request<T>(path, { ...options, retried: true });
    }
    for (const listener of unauthorizedListeners) {
      listener();
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const payload = text ? (safeJsonParse(text) ?? text) : undefined;

  if (!response.ok) {
    const error = payload as ApiError | undefined;

    if (response.status === 401) {
      session.clear();
      for (const listener of unauthorizedListeners) {
        listener();
      }
    }

    throw new ApiRequestError(
      response.status,
      error?.code ?? `http.${response.status}`,
      error?.message ?? response.statusText,
      error?.details,
      error?.requestId ?? response.headers.get('x-request-id') ?? undefined,
    );
  }

  return payload as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export const http = {
  get: <T>(path: string, query?: Record<string, unknown>, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),

  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body: body ?? {}, signal }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ?? {} }),

  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: body ?? {} }),

  delete: <T>(path: string, options?: { keepalive?: boolean }) =>
    request<T>(path, { method: 'DELETE', keepalive: options?.keepalive }),

  anonymousPost: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ?? {}, anonymous: true }),

  anonymousGet: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', anonymous: true, signal }),

  /**
   * A call made with an onboarding ticket rather than a session.
   *
   * `anonymous: true` keeps it out of the refresh-and-retry path: the ticket
   * has no refresh token and a 401 means the sign-up expired, which the caller
   * has to handle by sending the person back to the start.
   */
  onboardingGet: <T>(path: string, ticket: string) =>
    request<T>(path, { method: 'GET', anonymous: true, bearer: ticket }),

  onboardingPost: <T>(path: string, ticket: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body ?? {},
      anonymous: true,
      bearer: ticket,
    }),
};
